// Navigation Hook
// React hook for managing navigation state and interactions

import { useReducer, useCallback, useEffect } from 'react';
import { NavigationState, NavigationAction, NavigationConfig } from './types';

function navigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case 'SET_ACTIVE_ITEM':
      return {
        ...state,
        activeItemId: action.itemId
      };

    case 'TOGGLE_SECTION':
      const newExpandedSections = new Set(state.expandedSections);
      if (newExpandedSections.has(action.sectionId)) {
        newExpandedSections.delete(action.sectionId);
      } else {
        newExpandedSections.add(action.sectionId);
      }
      return {
        ...state,
        expandedSections: newExpandedSections
      };

    case 'TOGGLE_ITEM':
      const newExpandedItems = new Set(state.expandedItems);
      if (newExpandedItems.has(action.itemId)) {
        newExpandedItems.delete(action.itemId);
      } else {
        newExpandedItems.add(action.itemId);
      }
      return {
        ...state,
        expandedItems: newExpandedItems
      };

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        isCollapsed: !state.isCollapsed
      };

    case 'EXPAND_SECTION':
      const expandedSections = new Set(state.expandedSections);
      expandedSections.add(action.sectionId);
      return {
        ...state,
        expandedSections: expandedSections
      };

    case 'COLLAPSE_SECTION':
      const collapsedSections = new Set(state.expandedSections);
      collapsedSections.delete(action.sectionId);
      return {
        ...state,
        expandedSections: collapsedSections
      };

    default:
      return state;
  }
}

export const useNavigation = (config: NavigationConfig) => {
  const initialState: NavigationState = {
    activeItemId: config.defaultActiveItem || null,
    expandedSections: new Set(config.defaultExpandedSections || []),
    expandedItems: new Set(),
    isCollapsed: false
  };

  const [state, dispatch] = useReducer(navigationReducer, initialState);

  // Actions
  const setActiveItem = useCallback((itemId: string) => {
    dispatch({ type: 'SET_ACTIVE_ITEM', itemId });
  }, []);

  const toggleSection = useCallback((sectionId: string) => {
    dispatch({ type: 'TOGGLE_SECTION', sectionId });
  }, []);

  const toggleItem = useCallback((itemId: string) => {
    dispatch({ type: 'TOGGLE_ITEM', itemId });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const expandSection = useCallback((sectionId: string) => {
    dispatch({ type: 'EXPAND_SECTION', sectionId });
  }, []);

  const collapseSection = useCallback((sectionId: string) => {
    dispatch({ type: 'COLLAPSE_SECTION', sectionId });
  }, []);

  // Helper functions
  const isItemActive = useCallback((itemId: string) => {
    return state.activeItemId === itemId;
  }, [state.activeItemId]);

  const isSectionExpanded = useCallback((sectionId: string) => {
    return state.expandedSections.has(sectionId);
  }, [state.expandedSections]);

  const isItemExpanded = useCallback((itemId: string) => {
    return state.expandedItems.has(itemId);
  }, [state.expandedItems]);

  // Save state to localStorage for persistence
  useEffect(() => {
    const navigationState = {
      activeItemId: state.activeItemId,
      expandedSections: Array.from(state.expandedSections),
      expandedItems: Array.from(state.expandedItems),
      isCollapsed: state.isCollapsed
    };
    localStorage.setItem('minecraft-portal-navigation', JSON.stringify(navigationState));
  }, [state]);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('minecraft-portal-navigation');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.activeItemId) {
          dispatch({ type: 'SET_ACTIVE_ITEM', itemId: parsed.activeItemId });
        }
        if (parsed.expandedSections) {
          parsed.expandedSections.forEach((sectionId: string) => {
            dispatch({ type: 'EXPAND_SECTION', sectionId });
          });
        }
        if (parsed.isCollapsed) {
          dispatch({ type: 'TOGGLE_SIDEBAR' });
        }
      } catch (error) {
        console.warn('Failed to parse saved navigation state:', error);
      }
    }
  }, []);

  return {
    state,
    actions: {
      setActiveItem,
      toggleSection,
      toggleItem,
      toggleSidebar,
      expandSection,
      collapseSection
    },
    helpers: {
      isItemActive,
      isSectionExpanded,
      isItemExpanded
    }
  };
};